import { useState, useEffect, useContext } from "react";
import TopMenu from "./TopNav";
import SideNav from "./SideNav";
import GlobalModal from "../modals/GlobalModal/GlobalModal";
import { useAuth } from "context/auth";
import { useHistory, useParams } from "react-router-dom";
import OrganisationService from "service/organisationService";
import LocalStorageService from "service/localStorageService";
import AccountService from "service/accountService";
import {
    getLatestSubscription,
    getSubscriptionById,
    getWalletStatus,
} from "service/subscriptionService";
import { getAllTokens, isHttpSuccess } from "utils/functions";
import {
    changeOrganizationAction,
    storeOrganizationAction,
    storeUserAction,
    storeOgrActiveTabAction,
    verifyOrgOwnerAction,
    updateModalDataAction,
    storePanelsAction,
    storeTokenBalanceAction,
    updateSubscriptionStatus,
    storeSubscriptionID,
    storeOrgMembersAction,
    storeOrgGroupsAction,
    storeOrgRolesAction,
} from "store/actions";
import { EventKey, SubscriptionStatus } from "constant";
import { AppContext } from "context/appContext";
import useOrganizationVanilla, {
    changeToOrganization,
} from "hooks/organization";
import OrganizationWrapperModal from "../modals/OrganizationWrapperModal";
import { useWalletStore } from "hooks/wallet";
import { usePermissions } from "hooks/common";
import { OrganisationDto } from "types";
import moment from "moment";
import { getSubscriptionPlanType } from "components/subscriptions/CurrentPlanInformation";
import { getAPIError, showErrorAlert } from "utils/alert";
import { FETCH_ORGANIZATION_FAIL_MESSAGE } from "constant";

const Layout = (props: any) => {
    const [sideMenu, setSideMenu] = useState(false);
    const updateSideMenu = () => {
        setSideMenu(!sideMenu);
    };
    const history = useHistory();
    const params: any = useParams();
    const { signOut } = useAuth();
    const [orgLoaded, setOrgLoaded] = useState(false);
    const { storeData, storeDispatchActions } = useContext(AppContext);
    const { organization, user, globalModal, subscription } = storeData;
    const [subscriptionType, setSubscriptionType] = useState<any>("");

    usePermissions();

    const currentWallet = useWalletStore.getState().wallet;

    useEffect(() => {
        const { userData } = user;
        if (userData.email && !orgLoaded && !organization.isUserOrgLoaded) {
            OrganisationService.getUserOrgs().then((res) => {
                if (res.status === 200) {
                    const { owner, member } = res.data;

                    let currentOrg;
                    let orgList = [...owner, ...member].toCamelCase();

                    if (params.orgId) {
                        currentOrg = orgList.find(
                            (org: any) => org.uuid === params.orgId
                        );
                    }

                    if (!currentOrg) {
                        const orgId =
                            organization.currentOrgId ||
                            LocalStorageService.getItem("orgId");
                        currentOrg = orgList.find(
                            (org: any) => org.uuid === orgId
                        );

                        // If can not find anything will get from api response
                        if (!currentOrg) {
                            currentOrg = orgList[0];
                        }
                    }

                    LocalStorageService.setItem("orgIdList", orgList);
                    LocalStorageService.setItem("orgId", currentOrg.uuid);

                    setOrgLoaded(true);
                    storeDispatchActions(storeOrganizationAction({ orgList }));
                    storeDispatchActions(
                        changeOrganizationAction({
                            orgInfo: currentOrg,
                        })
                    );

                    const timezone = currentOrg.isDefault
                        ? userData.timezone
                        : currentOrg.timZone;
                    moment.tz(timezone);

                    storeDispatchActions(
                        verifyOrgOwnerAction({
                            isOwner: userData.email === currentOrg.owner,
                        })
                    );

                    storeDispatchActions(
                        storeOrgMembersAction({
                            orgMembers: currentOrg.members,
                        })
                    );

                    storeDispatchActions(
                        storeOrgGroupsAction({
                            orgGroups: currentOrg.groups,
                        })
                    );

                    storeDispatchActions(
                        storeOrgRolesAction({
                            orgRoles: currentOrg.roles,
                        })
                    );
                } else {
                    showErrorAlert(
                        getAPIError(res, FETCH_ORGANIZATION_FAIL_MESSAGE)
                    );
                }
            });
        }
    }, [
        orgLoaded,
        storeDispatchActions,
        user,
        params.orgId,
        organization.currentOrgId,
        organization.isUserOrgLoaded,
    ]);

    useEffect(() => {
        !user.isLoaded &&
            AccountService.getAccountInfo().then((response: any) => {
                if (response.status === 200) {
                    storeDispatchActions(
                        storeUserAction({
                            userData: response.data as object,
                            isLoaded: true,
                        })
                    );
                }
            });
    }, [user.isLoaded, storeDispatchActions]);

    useEffect(() => {
        if (!subscription.isLoaded) {
            getAllTokens().then((total: any) => {
                storeDispatchActions(
                    storeTokenBalanceAction({
                        tokenBalance: total,
                        isLoaded: true,
                    })
                );
            });
        }
    }, [subscription.isLoaded, storeDispatchActions, subscription]);

    useEffect(() => {
        if (!subscription.currentSubscriptionId) return;
        (async () => {
            const currentSubscription: any = await getSubscriptionById(
                subscription.currentSubscriptionId
            );
            if (isHttpSuccess(currentSubscription.status)) {
                const {
                    customer: { is_prepaid, is_cancellable },
                } = currentSubscription.data;
                setSubscriptionType(
                    getSubscriptionPlanType({
                        is_prepaid,
                        is_cancellable,
                    })
                );
            }
        })();
    }, [subscription.currentSubscriptionId]);

    useEffect(() => {
        getLatestSubscription()
            .then((latestSubscription: any) => {
                if (
                    [
                        SubscriptionStatus.ACTIVE,
                        SubscriptionStatus.CANCELED,
                        SubscriptionStatus.PENDING,
                    ].includes(latestSubscription.status)
                ) {
                    storeDispatchActions(
                        updateSubscriptionStatus({
                            isSubscribed: true,
                            hasSubscribed: true,
                        })
                    );
                    storeDispatchActions(
                        storeSubscriptionID(latestSubscription.uuid)
                    );
                } else {
                    storeDispatchActions(
                        updateSubscriptionStatus({
                            isSubscribed: false,
                            hasSubscribed: true,
                        })
                    );
                }
            })
            .catch((err: any) => {
                storeDispatchActions(
                    updateSubscriptionStatus({
                        isSubscribed: false,
                        hasSubscribed: false,
                    })
                );
            });
    }, [storeDispatchActions]);

    useEffect(() => {
        if (organization.currentOrgId) {
            getWalletStatus(organization.currentOrgId);
        }
    }, [organization.currentOrgId, currentWallet]);

    const handleChangeOrg = (
        currentOrg: OrganisationDto,
        isRedirect = true
    ) => {
        if (organization.currentOrgId === currentOrg.uuid) return;

        LocalStorageService.setItem("orgId", currentOrg.uuid);
        window.loadAuthorizationConfig().then(() => {
            switchTopNavUser(currentOrg);
            useOrganizationVanilla.setState({ orgId: currentOrg.uuid });
            changeToOrganization.setState({
                toOrganization: undefined,
            });

            isRedirect && history.push("/dashboard");
        });
    };

    useOrganizationVanilla.subscribe((state) => {
        if (state.isChangeOrganization === true && state.orgId) {
            state.setIsChangeOrganization(undefined);
            const organization =
                state.organizationList.find(
                    (org) => org.uuid === state.orgId
                ) || {};
            organization && handleChangeOrg(organization, false);
        }
    });

    const switchTopNavUser = (organization: OrganisationDto) => {
        const { userData } = user;
        storeDispatchActions(
            changeOrganizationAction({
                orgInfo: organization,
            })
        );

        storeDispatchActions(
            verifyOrgOwnerAction({
                isOwner: userData.email === organization.owner,
            })
        );

        storeDispatchActions(
            storeOgrActiveTabAction({
                tab: EventKey.members,
            })
        );

        storeDispatchActions(storePanelsAction({ panels: [] }));
    };

    const handleCloseModal = () => {
        if (globalModal.redirectTo) history.push(globalModal.redirectTo);

        storeDispatchActions(
            updateModalDataAction({
                isShow: false,
                mainMessage: "",
                subMessage: [],
            })
        );
    };

    return (
        <div>
            <TopMenu
                logout={signOut}
                sideMenu={sideMenu}
                updateSideMenu={updateSideMenu}
                userInfo={user.userData}
                orgList={organization.orgList}
                currentOrgId={organization.currentOrgId}
                currentOrgInfo={organization.currentOrgInfo}
                onOrgChange={handleChangeOrg}
                subscription={subscription}
                subscriptionType={subscriptionType}
            />

            <SideNav sideMenu={sideMenu} updateSideMenu={updateSideMenu}>
                <OrganizationWrapperModal>
                    {props.children}
                </OrganizationWrapperModal>
            </SideNav>

            <GlobalModal
                isShow={globalModal.isShow}
                mainMessage={globalModal.mainMessage}
                subMessage={globalModal.subMessage}
                onClose={() => handleCloseModal()}
            />
        </div>
    );
};

export default Layout;
